'use client'

import { useState, useRef } from 'react'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Mousewheel, Navigation, A11y } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'

import IconButton from '/react4xp/components/prototype/IconButton'
import ArrowRightSolid from '/react4xp/components/prototype/icons/ArrowRightSolid'

import type { CardCarouselProps, CardCarouselCard } from './types'

// Column capacity per breakpoint: 4 on 2xl, 3 on xl, 2 on lg/md, 1 on mobile.
// Cards render as a grid from the first breakpoint whose capacity covers them,
// and as the scrolling carousel below it — inline when the row fits, carousel
// when it doesn't. The grid's own column count keeps tracking the breakpoint's
// full capacity even after it activates, so e.g. 2 cards sit at half-width on
// md/lg but shrink to quarter-width on 2xl (matching the 4-col slot size)
// rather than staying stretched to whatever count first unlocked the grid.
// Counts above 4 never fit any tier, so they're always the carousel.
function getGridDisplay(cardCount: number) {
	if (cardCount <= 1) {
		return {
			gridClass: 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8',
			carouselHiddenClass: 'hidden',
		}
	}
	if (cardCount === 2) {
		return {
			gridClass: 'hidden md:grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8',
			carouselHiddenClass: 'md:hidden',
		}
	}
	if (cardCount === 3) {
		return {
			gridClass: 'hidden xl:grid xl:grid-cols-3 2xl:grid-cols-4 gap-8',
			carouselHiddenClass: 'xl:hidden',
		}
	}
	if (cardCount === 4) {
		return {
			gridClass: 'hidden 2xl:grid 2xl:grid-cols-4 gap-8',
			carouselHiddenClass: '2xl:hidden',
		}
	}
	return { gridClass: '', carouselHiddenClass: '' }
}

function CarouselCard({ image, overline, title }: CardCarouselCard) {
	return (
		<div className="flex flex-col gap-4 w-full text-black-950">
			{image ? (
				<figure className="w-full aspect-square overflow-hidden">
					<img
						src={image.src}
						alt={image.alt ?? ''}
						className="w-full h-full object-cover"
					/>
				</figure>
			) : (
				<div className="w-full aspect-square" aria-hidden="true" />
			)}

			<div className="flex flex-col gap-2">
				{overline ? (
					<p className="body-small uppercase">{overline}</p>
				) : (
					<p className="body-small" aria-hidden="true">&nbsp;</p>
				)}
				{title && <h3 className="h4 text-black-950 line-clamp-4">{title}</h3>}
			</div>
		</div>
	)
}

export default function CardCarousel({
	overline,
	title,
	cards = [],
	prevLabel = 'Previous slide',
	nextLabel = 'Next slide',
}: CardCarouselProps) {
	const swiperRef = useRef<SwiperType | null>(null)
	const [isBeginning, setIsBeginning] = useState(true)
	const [isEnd, setIsEnd] = useState(false)

	function handleSwiperUpdate(swiper: SwiperType) {
		setIsBeginning(swiper.isBeginning)
		setIsEnd(swiper.isEnd)
	}

	const { gridClass, carouselHiddenClass } = getGridDisplay(cards.length)

	return (
		<section className="overflow-hidden my-16">
			<div className="container">

				<div className="flex flex-col">
					{overline && <p className="body text-black-950">{overline}</p>}
					{title && <h2 className="h2 text-black-950 max-w-175">{title}</h2>}
				</div>

				{cards.length > 0 && (
					<div className="flex flex-col gap-8 mt-8">

						{gridClass && (
							<div className={gridClass}>
								{cards.map((card, i) => (
									<CarouselCard
										key={i}
										image={card.image}
										overline={card.overline}
										title={card.title}
									/>
								))}
							</div>
						)}

						<div className={`flex items-center justify-end gap-2 ${carouselHiddenClass}`}>
							<IconButton
								icon={ArrowRightSolid}
								label={prevLabel}
								variant="round"
								disabled={isBeginning}
								onClick={() => swiperRef.current?.slidePrev()}
								className="rotate-180"
							/>
							<IconButton
								icon={ArrowRightSolid}
								label={nextLabel}
								variant="round"
								disabled={isEnd}
								onClick={() => swiperRef.current?.slideNext()}
							/>
						</div>

						<div className={`carousel-track full-bleed min-w-0 2xl:w-full 2xl:mx-0 ${carouselHiddenClass}`}>
							<Swiper
								modules={[Mousewheel, Navigation, A11y]}
								mousewheel={{ forceToAxis: true }}
								slidesPerView="auto"
								spaceBetween={32}
								slidesOffsetBefore={12}
								slidesOffsetAfter={0}
								breakpoints={{
									768: { slidesOffsetBefore: 24, slidesOffsetAfter: 24 },
									1024: { slidesOffsetBefore: 64, slidesOffsetAfter: 64 },
									1536: { slidesOffsetBefore: 0, slidesOffsetAfter: 0 },
								}}
								onSwiper={(swiper) => {
									swiperRef.current = swiper
									handleSwiperUpdate(swiper)
								}}
								onSlideChange={(swiper) => handleSwiperUpdate(swiper)}
								onReachBeginning={(swiper) => handleSwiperUpdate(swiper)}
								onReachEnd={(swiper) => handleSwiperUpdate(swiper)}
								onFromEdge={(swiper) => handleSwiperUpdate(swiper)}
								onResize={(swiper) => handleSwiperUpdate(swiper)}
							>
								{cards.map((card, i) => (
									<SwiperSlide key={i} className="card-carousel-slide">
										<CarouselCard
											image={card.image}
											overline={card.overline}
											title={card.title}
										/>
									</SwiperSlide>
								))}
							</Swiper>
						</div>

					</div>
				)}

			</div>
		</section>
	)
}
